import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Calendar,
  Clock,
  Send,
  X,
  Loader2,
  Users,
  Mail,
  Trash2,
  Play,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const ScheduledCampaigns = ({ onRefresh }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchScheduledCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email/scheduled-campaigns`);
      if (res.data.success) {
        setCampaigns(res.data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching scheduled campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScheduledCampaigns();
  }, [fetchScheduledCampaigns]);

  const handleCancel = async (campaignId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled campaign?')) {
      return;
    }
    
    setActionLoading(campaignId);
    try {
      const res = await axios.post(`${API}/admin/email/campaign/${campaignId}/cancel`);
      if (res.data.success) {
        toast.success('Campaign cancelled');
        fetchScheduledCampaigns();
        onRefresh?.();
      } else {
        toast.error(res.data.error || 'Failed to cancel campaign');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel campaign');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendNow = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign now?')) {
      return;
    }
    
    setActionLoading(campaignId);
    try {
      const res = await axios.post(`${API}/admin/email/campaign/${campaignId}/send-now`);
      if (res.data.success) {
        toast.success('Campaign started sending');
        fetchScheduledCampaigns();
        onRefresh?.();
      } else {
        toast.error(res.data.error || 'Failed to start campaign');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start campaign');
    } finally {
      setActionLoading(null);
    }
  };

  const formatScheduledTime = (isoDate) => {
    if (!isoDate) return 'Not set';
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getTimeUntil = (isoDate) => {
    if (!isoDate) return '';
    try {
      const now = new Date();
      const scheduled = new Date(isoDate);
      if (isNaN(scheduled.getTime())) return '';
      const diff = scheduled - now;
      
      if (diff < 0) return 'Due now';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `in ${days} day${days > 1 ? 's' : ''}`;
      } else if (hours > 0) {
        return `in ${hours}h ${minutes}m`;
      } else {
        return `in ${minutes}m`;
      }
    } catch (e) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="bg-[#4A0A66]/10 rounded-xl p-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#df1283]" />
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="bg-[#4A0A66]/10 rounded-xl p-4" data-testid="scheduled-campaigns-empty">
        <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#0FFFE2]" />
          Scheduled Campaigns
        </h4>
        <div className="text-center py-6 text-white/50">
          <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No scheduled campaigns</p>
          <p className="text-xs">Use the Bulk Campaign feature to schedule emails</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#4A0A66]/10 rounded-xl p-4" data-testid="scheduled-campaigns-list">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-white flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#0FFFE2]" />
          Scheduled Campaigns ({campaigns.length})
        </h4>
        <Button
          data-testid="scheduled-campaigns-refresh"
          aria-label="Refresh scheduled campaigns"
          onClick={fetchScheduledCampaigns}
          variant="ghost"
          size="sm"
          className="text-white/50 hover:text-white h-8 w-8 p-0"
        >
          <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {campaigns.map((campaign) => (
          <div 
            key={campaign.campaign_id}
            className="bg-[#4A0A66]/20 rounded-lg p-3 border border-[#4A0A66]/30"
            data-testid={`scheduled-campaign-${campaign.campaign_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {campaign.subject || campaign.campaign_name || 'Untitled Campaign'}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-white/50">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {campaign.total_recipients} recipients
                  </span>
                  <span className="flex items-center gap-1 text-[#0FFFE2]">
                    <Clock className="w-3 h-3" />
                    {getTimeUntil(campaign.scheduled_for)}
                  </span>
                </div>
                <div className="text-xs text-white/40 mt-1">
                  {formatScheduledTime(campaign.scheduled_for)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  onClick={() => handleSendNow(campaign.campaign_id)}
                  disabled={actionLoading === campaign.campaign_id}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
                  title="Send Now"
                  data-testid={`send-now-${campaign.campaign_id}`}
                >
                  {actionLoading === campaign.campaign_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  onClick={() => handleCancel(campaign.campaign_id)}
                  disabled={actionLoading === campaign.campaign_id}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/20"
                  title="Cancel"
                  data-testid={`cancel-${campaign.campaign_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScheduledCampaigns;
